import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../admin/components/ConditionalAccess';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { copyToClipboard, getSocialMediaPlatformLogoUrl, sortPlatformTargets } from '../../../../models/UiUtils';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { brandPrimaryHover } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { CancelIconFill } from '../../svgs/icons/CancelIconFill';
import { ClipboardIcon } from '../../svgs/icons/ClipboardIcon';
import { DangerIcon } from '../../svgs/icons/DangerIcon';
import { HeartIcon } from '../../svgs/icons/HeartIcon';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { MessageCircleIcon } from '../../svgs/icons/MessageCircleIcon';
import { ShareIcon } from '../../svgs/icons/ShareIcon';
import { PopoverType, TinyPopover } from '../../TinyPopover';
import { SimpleTooltip } from '../../Tooltip';
import { SocialMediaPostTargetStatusIndicators } from '../SocialMediaPostTargetStatusIndicators';
import { SHOW_ENGAGEMENT_COLUMN, styleSheet } from './styles';

const formatCount = (value?: number) => {
	if (value == null) return '—';
	return value.toLocaleString('en-US');
};

// LinkedIn only exposes engagement metrics for organization pages. We only show stats
// when the URN-style postTargetId positively identifies an organization
// (`urn:li:organization:<id>`); everything else (personal profiles, unknown formats,
// bare IDs) is treated as unsupported so we don't display empty/zero numbers that
// would read as "no engagement" rather than "we can't see this".
const isUnsupportedLinkedInTarget = (target: Api.IPostTarget) =>
	target.provider === Api.SocialMediaType.LinkedIn &&
	!/(^|:)organization(:|$)/i.test(target.state?.lastConnectionState?.postTargetId ?? '');

interface IEngagementStatProps {
	icon: React.ReactNode;
	iconWrapperStyle: StyleDeclarationValue;
	label: string;
	value?: number;
}

const EngagementStat = ({ icon, iconWrapperStyle, label, value }: IEngagementStatProps) => (
	<SimpleTooltip title={label}>
		<span className={css(styleSheet.engagementStat)}>
			<span className={css(iconWrapperStyle)}>{icon}</span>
			<span className={css(styleSheet.engagementValue)}>{formatCount(value)}</span>
		</span>
	</SimpleTooltip>
);

const LinkedInUnsupportedCell = () => (
	<SimpleTooltip title='LinkedIn engagement metrics are currently unavailable for personal pages.'>
		<span className={css(styleSheet.engagementEmpty)}>Data unavailable for this post</span>
	</SimpleTooltip>
);

const EngagementCell = observer(({ target }: { target: Api.IPostTarget }) => {
	if (isUnsupportedLinkedInTarget(target)) {
		return <LinkedInUnsupportedCell />;
	}

	const metadata = target.metadata ?? {};
	// Instagram only surfaces likes (no multi-emoji reactions), so for IG targets
	// the heart slot reads from metadata.likes instead of metadata.reactions.
	const reactionsValue = target.provider === Api.SocialMediaType.Instagram ? metadata.likes : metadata.reactions;

	const stats: IEngagementStatProps[] = [
		{
			icon: <HeartIcon width={18} height={18} filled={true} />,
			iconWrapperStyle: styleSheet.engagementIconReactions,
			label: 'Reactions',
			value: reactionsValue,
		},
		{
			icon: <MessageCircleIcon width={18} height={18} />,
			iconWrapperStyle: styleSheet.engagementIconComments,
			label: 'Comments',
			value: metadata.comments,
		},
		{
			icon: <ShareIcon width={18} height={18} />,
			iconWrapperStyle: styleSheet.engagementIconShares,
			label: 'Shares',
			value: metadata.shares,
		},
	];

	if (!stats.some(s => s.value != null)) {
		return <span className={css(styleSheet.engagementEmpty)}>No data yet</span>;
	}

	return (
		<>
			{stats.map(stat => (
				<EngagementStat key={stat.label} {...stat} />
			))}
		</>
	);
});

export interface ISocialMediaReportingTableRowProps {
	report: Api.SocialMediaPostReportViewModel;
	onTitleClick?: (report: Api.SocialMediaPostReportViewModel) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const SocialMediaReportingTableRow = observer(
	({ report, onTitleClick, impersonationContext }: ISocialMediaReportingTableRowProps) => {
		const { logInput, logApiError } = useEventLogging('SocialMediaPostReconnectionAttempt');
		const [isOpen, , setIsOpen] = useLambda(false);
		const userSession = useUserSession();
		const [showFailedPopover, , setShowFailedPopover] = useLambda(false);

		const getStatusStyle = (status: Api.PostStatus) => {
			return (styleSheet as any)[`status${status}`] as StyleDeclarationValue;
		};

		const onReconnectLinkClicked = React.useCallback(() => {
			const { origin, pathname } = window.location;
			window?.open(`${origin}${pathname}#/settings/social-media/social-media`, '_blank');
			logInput('ReconnectAttemptLink', 'Click');
		}, [logInput]);

		const generatePostUrl = async (target: Api.IPostTarget) => {
			const opResult = await userSession.webServiceHelper.callWebServiceAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						provider: target.provider,
					},
					urlPath: `social/post/${report.id}/generateProviderLink`,
				}),
				'POST',

				target.state.lastConnectionState
			);
			if (opResult.success) {
				window?.open(opResult.value, '_blank');
			}
		};

		const getDateToDisplay = (statusDefault?: boolean) => {
			switch (!statusDefault && report.status) {
				case Api.PostStatus.Succeeded:
					return moment(report?.completedDate || report.dueDate).format('MM/DD/YYYY');
				case Api.PostStatus.Cancelled:
					return moment(report?.cancelledDate || report.lastModifiedDate).format('MM/DD/YYYY');
				default:
					return moment(report.dueDate).format('MM/DD/YYYY');
			}
		};

		const getTimeToDisplay = (statusDefault?: boolean) => {
			switch (!statusDefault && report.status) {
				case Api.PostStatus.Succeeded:
					return moment(report?.completedDate || report.dueDate).format('h:mma');
				case Api.PostStatus.Cancelled:
					return moment(report?.cancelledDate || report.lastModifiedDate).format('h:mma');
				default:
					return moment(report.dueDate).format('h:mma');
			}
		};

		const onEditSocialPost = async (notification: ILocalNotification<Api.SocialMediaPostViewModel>) => {
			if (notification.info?.id === report.id) {
				try {
					await report.load();
				} catch (error) {
					logApiError('SocialMediaPostReportLoad-Error', error);
				}
			}
		};

		const displayTargets = React.useMemo(() => {
			if (report?.designatedTargets?.length > 0) {
				return sortPlatformTargets(report?.designatedTargets);
			}

			return sortPlatformTargets(report?.targets) || [];
		}, [report?.designatedTargets, report?.targets]);

		const hasConnectionInfo = displayTargets && displayTargets.filter(x => x.state?.lastConnectionState).length > 0;

		// to asssign a reconnect indicator verify that the post is still in process and check that either new or old targets have a disconnected target
		const isReconnect =
			(report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending) &&
			displayTargets?.find(
				x =>
					x.state?.lastConnectionState?.state !== Api.SocialMediaConnectionState.Connected ||
					!x.state?.lastConnectionState?.postTargetId
			);

		const handleCopyId = (ev: React.MouseEvent<HTMLElement>) => {
			ev.stopPropagation();
			copyToClipboard(report.id ?? '');

			toast.success('ID copied to clipboard');
		};

		const handleViewPost = (postTarget: Api.IPostTarget) => () => generatePostUrl(postTarget);

		return (
			<li className={css(styleSheet.row)}>
				<div className={css(styleSheet.nameColumn, styleSheet.nameColumnRow)}>
					<div className={css(styleSheet.rowName)}>
						<div className={css(styleSheet.rowTitle)}>
							<button
								className={css(baseStyleSheet.brandLink, baseStyleSheet.truncateText)}
								onClick={onTitleClick != null ? () => onTitleClick(report) : undefined}
							>
								<div className={css(baseStyleSheet.truncateText)}>{report.name || 'Custom Name'}</div>
							</button>
							<ConditionalAccess permission={AdminPermission.ReadAccount}>
								<span onClick={handleCopyId} className={css(styleSheet.rowClipboardParent)}>
									<ClipboardIcon />
								</span>
							</ConditionalAccess>
						</div>
						<div className={css(styleSheet.rowDetails)}>
							<div className={css(styleSheet.rowDetailsRow)}>
								<div className={css(styleSheet.rowDetailsStatus, getStatusStyle(report.status))}>
									{report.status === Api.PostStatus.PartiallySucceeded
										? 'Partial Success'
										: report.status === Api.PostStatus.Succeeded
											? 'Posted'
											: report.status}
								</div>
								&nbsp;
								{report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending ? 'for ' : 'on'}
								<span>
									&nbsp;
									<span className={css(baseStyleSheet.fontBold, styleSheet.dateText)}>{getDateToDisplay()}</span>
									{' at'}
									&nbsp;
									{getTimeToDisplay()}
								</span>
							</div>
							<div className={css(styleSheet.rowDetailsRow)}>
								<div className={css(baseStyleSheet.truncateText)}>
									{`by ${report.creator?.firstName} ${report.creator?.lastName}`}
								</div>
							</div>
							{(((report.status === Api.PostStatus.Scheduled || report.status === Api.PostStatus.Pending) &&
								displayTargets?.find(
									x =>
										x.state?.lastConnectionState?.state !== Api.SocialMediaConnectionState.Connected ||
										!x.state?.lastConnectionState?.postTargetId
								)) ||
								report.status === Api.PostStatus.Cancelled) && (
								<div className={css(styleSheet.popover)}>
									<TinyPopover
										anchor={
											<div
												className={css(
													baseStyleSheet.flex,
													baseStyleSheet.itemsCenter,
													styleSheet.detailed,
													isReconnect && styleSheet.reconnect
												)}
												onMouseEnter={setIsOpen(true)}
												onMouseLeave={setIsOpen(false)}
											>
												{isReconnect ? (
													<DangerIcon />
												) : (
													<InfoIcon className={css(styleSheet.icon)} fillColor={brandPrimaryHover} />
												)}
												{isReconnect ? 'Action Needed' : 'Campaign Status'}
											</div>
										}
										anchorStyles={[styleSheet.detailed]}
										dismissOnOutsideAction={true}
										autoCloseOtherPopoversOnHover={true}
										type={isReconnect ? PopoverType.errorSecondary : PopoverType.background}
										isOpen={isOpen}
										placement={['right', 'bottom', 'top']}
										align='center'
									>
										<p className={css(styleSheet.popoverContent)}>
											{isReconnect
												? 'One or more of the post targets is not connected.'
												: `This campaign was scheduled for ${getDateToDisplay(true)} at ${getTimeToDisplay(true)}.`}
										</p>
									</TinyPopover>
									{isReconnect && report?.canEdit && (
										<button
											className={css(baseStyleSheet.ctaButton, styleSheet.reconnectButton)}
											onClick={onReconnectLinkClicked}
										>
											Reconnect
										</button>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className={css(styleSheet.createdColumn, styleSheet.rowResponses)}>
					{report.creationDate ? format(new Date(report.creationDate), 'MM/dd/yyyy') : 'N/A'}
				</div>
				<div className={css(styleSheet.platformColumn, styleSheet.rowResponses)}>
					<div className={css(styleSheet.platformAccounts)}>
						{hasConnectionInfo ? (
							displayTargets?.map((x: Api.IPostTarget, i: number) => (
								<div
									className={css(styleSheet.platformIconRow)}
									key={`${x.provider}::${x.state?.lastConnectionState?.postTargetId ?? `idx-${i}`}`}
								>
									{x.state?.lastConnectionState ? (
										<>
											<img src={getSocialMediaPlatformLogoUrl(x.provider)} />
											<div className={css(styleSheet.connectionStatus, styleSheet.platformText)}>
												{x.state?.lastConnectionState?.userName}
												{x.state?.lastConnectionState && ' /'}{' '}
												{x.state?.lastConnectionState?.postTargetDisplayName || x.provider}
												<SocialMediaPostTargetStatusIndicators
													postStatus={report.status}
													postTarget={x}
													showTargetProvider={true}
												/>
											</div>
										</>
									) : (
										<>
											<img src={getSocialMediaPlatformLogoUrl(x.provider)} />
											<div className={css(styleSheet.connectionStatus, styleSheet.platformText)}>
												(Unknown User / Unknown Page)
												<SocialMediaPostTargetStatusIndicators
													postStatus={report.status}
													postTarget={x}
													showTargetProvider={true}
												/>
											</div>
										</>
									)}
								</div>
							))
						) : (
							<div className={css(styleSheet.connectionStatus, styleSheet.platformText)}>
								{' '}
								Originally scheduled for {Array.from(new Set(displayTargets.map(x => x.provider))).join(', ')}
								<br />
								<TinyPopover
									align='end'
									anchor={
										<div
											onMouseEnter={setShowFailedPopover(true)}
											onMouseLeave={setShowFailedPopover(false)}
											className={css(baseStyleSheet.flex)}
										>
											<CancelIconFill />
										</div>
									}
									styles={[styleSheet.popoverMargin]}
									dismissOnOutsideAction={true}
									isOpen={showFailedPopover}
									placement={['right', 'top', 'left', 'bottom']}
									type={PopoverType.errorSecondary}
								>
									<div className={css(styleSheet.popoverWarning)}>No account info present. Please reconnect.</div>
								</TinyPopover>
							</div>
						)}
					</div>
				</div>
				{SHOW_ENGAGEMENT_COLUMN && (
					<div className={css(styleSheet.engagementColumn, styleSheet.rowResponses)}>
						<div className={css(styleSheet.engagementStack)}>
							{!hasConnectionInfo ? (
								// Single placeholder row mirrors the platforms column's fallback when targets
								// have lost their connection info, keeping the two columns vertically aligned.
								<div className={css(styleSheet.engagementRow)}>
									<span className={css(styleSheet.engagementEmpty)}>—</span>
								</div>
							) : (
								displayTargets?.map((x: Api.IPostTarget, i: number) => (
									<div
										className={css(styleSheet.engagementRow)}
										key={`engagement-${x.provider}-${x.state?.lastConnectionState?.postTargetId ?? i}`}
									>
										<EngagementCell target={x} />
									</div>
								))
							)}
						</div>
					</div>
				)}
				<div className={css(styleSheet.viewPostColumn, styleSheet.rowResponses)}>
					<div className={css(styleSheet.platformAccounts)}>
						{displayTargets?.map((x: Api.IPostTarget, i: number) => (
							<div
								className={css(styleSheet.platformIconRow)}
								key={`${x.provider}-${x.state?.lastConnectionState?.postTargetId ?? i}`}
							>
								<img src={getSocialMediaPlatformLogoUrl(x.provider)} />
								<div className={css(styleSheet.connectionStatus)}>
									{x.state?.status === Api.PostTargetStatus.Posted ? (
										<button
											className={css(baseStyleSheet.brandLink, styleSheet.platformText)}
											onClick={handleViewPost(x)}
										>
											View Post
										</button>
									) : (
										<>Not Available</>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
				<LocalNotificationObserver topic={Topics.EDIT_SOCIAL_POST} onNotificationReceived={onEditSocialPost} />
			</li>
		);
	}
);
